import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Link, useSearchParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { GiftPreview } from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import useConfirmChanges from "components/admin/confirmChanges";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber, toLocalInput } from "utils/format";
import RewardTierDialog, { KIND_NAMES } from "layouts/mythic-arena/RewardTierDialog";
import SettlementDialog from "layouts/mythic-arena/SettlementDialog";
import TestSettleDialog from "layouts/mythic-arena/TestSettleDialog";

const RANKING_PAGE_SIZE = 20;

const rankText = (from, to) => {
  if (to === 0) return `Hạng ${from} trở xuống`;
  return from === to ? `Hạng ${from}` : `Hạng ${from} - ${to}`;
};

function leftText(target) {
  let seconds = Math.max(0, Math.floor((new Date(target).getTime() - Date.now()) / 1000));
  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `còn ${days} ngày ${hours} giờ`;
  if (hours > 0) return `còn ${hours} giờ ${minutes} phút`;
  return `còn ${minutes} phút`;
}

function ConfigCard({ overview, canEdit, onSaved }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const confirm = useConfirm();
  const [form, setForm] = useState(overview.config);
  const [endAt, setEndAt] = useState(() => toLocalInput(overview.season?.endAt));

  useEffect(() => setForm(overview.config), [overview.config]);
  useEffect(() => setEndAt(toLocalInput(overview.season?.endAt)), [overview.season?.endAt]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const saveConfig = async () => {
    const turningOff = overview.config.enabled && !form.enabled;
    const yes = await ask({
      title: "Lưu giờ chốt thưởng",
      before: overview.config,
      after: form,
      fields: [
        ["dailyTime", "Giờ chốt mỗi ngày", { text: (v) => v }],
        ["seasonDays", "Số ngày mỗi mùa"],
        ["enabled", "Chốt thưởng"],
      ],
      notes: [
        turningOff &&
          "⚠ TẮT chốt thưởng: không ai nhận thưởng ngày, mùa không kết thúc. Bật lại sau KHÔNG chốt bù những ngày đã tắt.",
        "Số ngày mỗi mùa chỉ áp dụng từ mùa sau.",
      ],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put("/api/admin/mythic-arena/config", {
          dailyTime: form.dailyTime,
          seasonDays: Number(form.seasonDays),
          enabled: Boolean(form.enabled),
        }),
      (text) => text
    );
    if (ok) onSaved();
  };
  const saveSeason = async () => {
    const yes = await confirm({
      title: `Đổi ngày kết thúc mùa ${overview.season?.id}`,
      message: [
        `Kết thúc: ${formatDate(overview.season?.endAt)} → ${formatDate(endAt)}.`,
        "",
        "Lúc kết thúc, máy chủ chốt thưởng mùa theo bảng hạng lúc đó và kéo cúp mọi người về gần 1000.",
        `Mùa sau dài ${overview.config.seasonDays} ngày tính từ lúc này.`,
      ].join("\n"),
      color: "warning",
      confirmText: "Đổi ngày kết thúc",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put("/api/admin/mythic-arena/season", { endAt }),
      (text) => text
    );
    if (ok) onSaved();
  };

  const { season } = overview;
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <PageCard title="Giờ chốt thưởng" subtitle="Áp dụng cho cả thưởng ngày và thưởng mùa">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                type="time"
                label="Giờ chốt mỗi ngày"
                value={form.dailyTime}
                disabled={!canEdit}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => set({ dailyTime: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                type="number"
                label="Số ngày mỗi mùa"
                value={form.seasonDays}
                disabled={!canEdit}
                inputProps={{ min: 1 }}
                onChange={(e) => set({ seasonDays: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center">
                <Switch
                  checked={Boolean(form.enabled)}
                  disabled={!canEdit}
                  onChange={(e) => set({ enabled: e.target.checked })}
                />
                <MDTypography variant="button">
                  {form.enabled ? "Đang bật chốt thưởng" : "Đang tắt chốt thưởng"}
                </MDTypography>
              </MDBox>
              <MDTypography variant="caption" color="text" display="block">
                {overview.config.enabled
                  ? `Lần chốt ngày tới: ${formatDate(overview.nextDailyCutoff)} (${leftText(
                      overview.nextDailyCutoff
                    )})`
                  : "Đang tắt: không chốt ngày, không kết thúc mùa. Bật lại sẽ không chốt bù khoảng thời gian đã tắt."}
              </MDTypography>
            </Grid>
            {canEdit && (
              <Grid item xs={12} textAlign="right">
                <MDButton color="info" size="small" disabled={busy} onClick={saveConfig}>
                  <Icon>save</Icon>&nbsp;Lưu…
                </MDButton>
              </Grid>
            )}
          </Grid>
        </PageCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <PageCard
          title={season ? `Mùa ${season.id}` : "Mùa đấu trường"}
          subtitle="Mùa kết thúc ở lần chốt ngày cuối, cúp được kéo về gần 1000"
          color="success"
        >
          {!season ? (
            <Alert severity="warning">Chưa có mùa nào, server sẽ tạo khi khởi động.</Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDTypography variant="button" display="block">
                  Bắt đầu: {formatDate(season.startAt)}
                </MDTypography>
                <MDTypography variant="button" display="block">
                  Kết thúc: {formatDate(season.endAt)} ({leftText(season.endAt)})
                </MDTypography>
              </Grid>
              {canEdit && (
                <>
                  <Grid item xs={12} sm={8}>
                    <MDInput
                      fullWidth
                      type="datetime-local"
                      label="Đổi thời điểm kết thúc mùa này"
                      value={endAt}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setEndAt(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} textAlign="right">
                    <MDButton
                      color="success"
                      size="small"
                      disabled={busy || !endAt || endAt === toLocalInput(overview.season?.endAt)}
                      onClick={saveSeason}
                    >
                      <Icon>event</Icon>&nbsp;Đổi ngày kết thúc
                    </MDButton>
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text">
                      Nên đặt trùng giờ chốt ngày để thưởng ngày và thưởng mùa ra cùng một bảng
                      hạng. Mùa sau dài {overview.config.seasonDays} ngày tính từ lúc mùa này kết
                      thúc.
                    </MDTypography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </PageCard>
      </Grid>
    </Grid>
  );
}

ConfigCard.propTypes = {
  overview: PropTypes.shape({
    config: PropTypes.object,
    season: PropTypes.object,
    nextDailyCutoff: PropTypes.string,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function RewardsCard({ rewards, canEdit, onChanged }) {
  const [kind, setKind] = useState(0);
  const [editing, setEditing] = useState(null);
  const [run] = useRunner();
  const confirm = useConfirm();
  const rows = rewards.filter((t) => t.kind === kind);
  const last = rows.reduce((max, t) => Math.max(max, t.rankTo || t.rankFrom), 0);

  const remove = async (t) => {
    const yes = await confirm({
      title: "Xoá mốc thưởng",
      message: `Xoá mốc ${rankText(t.rankFrom, t.rankTo).toLowerCase()} của ${KIND_NAMES[
        t.kind
      ].toLowerCase()}? Muốn ngừng tạm thời thì tắt "Đang áp dụng" thay vì xoá.`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.del(`/api/admin/mythic-arena/rewards/${t.id}`),
      (text) => text
    );
    if (ok) onChanged();
  };

  return (
    <PageCard
      title="Mốc thưởng hạng"
      subtitle="Gửi vào thư ở mỗi lần chốt, hạng tính theo bảng hạng hiển thị trong game"
      color="warning"
      noPadding
      actions={
        canEdit && (
          <MDButton size="small" color="white" onClick={() => setEditing("new")}>
            <Icon>add</Icon>&nbsp;Thêm mốc
          </MDButton>
        )
      }
    >
      <MDBox px={2}>
        <Tabs value={kind} onChange={(e, v) => setKind(v)}>
          <Tab value={0} label={KIND_NAMES[0]} />
          <Tab value={1} label={KIND_NAMES[1]} />
        </Tabs>
      </MDBox>
      <AdminTable
        rows={rows}
        emptyText="Chưa có mốc nào, lần chốt tới sẽ không ai nhận quà"
        columns={[
          {
            key: "rank",
            label: "Khoảng hạng",
            render: (t) => (
              <MDTypography variant="button" fontWeight="medium">
                {rankText(t.rankFrom, t.rankTo)}
              </MDTypography>
            ),
          },
          { key: "gift", label: "Quà", wrap: true, render: (t) => <GiftPreview lines={t.lines} /> },
          {
            key: "enabled",
            label: "Trạng thái",
            render: (t) => (
              <Chip
                size="small"
                color={t.enabled ? "success" : "default"}
                label={t.enabled ? "Đang áp dụng" : "Tắt"}
              />
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (t) => (
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton size="small" variant="text" color="warning" onClick={() => setEditing(t)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
                <MDButton size="small" variant="text" color="error" onClick={() => remove(t)}>
                  <Icon>delete</Icon>
                </MDButton>
              </MDBox>
            ),
          },
        ].filter(Boolean)}
      />
      {editing && (
        <RewardTierDialog
          tier={editing === "new" ? null : editing}
          kind={kind}
          nextFrom={last + 1}
          onClose={() => setEditing(null)}
          onDone={onChanged}
        />
      )}
    </PageCard>
  );
}

RewardsCard.propTypes = {
  rewards: PropTypes.arrayOf(PropTypes.object).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function SettlementsCard({ settlements, canEdit, onChanged }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [viewing, setViewing] = useState(null);
  const [testing, setTesting] = useState(false);
  const missing = settlements.some((s) => s.sent < s.rewarded);

  const resend = async () => {
    const lacking = settlements.reduce((n, s) => n + Math.max(0, s.rewarded - s.sent), 0);
    const yes = await confirm({
      title: "Gửi lại thư thiếu",
      message:
        lacking > 0
          ? `Gửi ${formatNumber(
              lacking
            )} thư thưởng còn thiếu ở các lần chốt trước. Người đã có thư sẽ không nhận thêm.`
          : "Hiện không thấy lần chốt nào thiếu thư. Vẫn cho máy chủ kiểm tra lại? Người đã có thư sẽ không nhận thêm.",
      confirmText: "Gửi lại",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.post("/api/admin/mythic-arena/settlements/resend"),
      (text) => text
    );
    if (ok) setTimeout(onChanged, 3000);
  };

  return (
    <PageCard
      title="Lịch sử chốt thưởng"
      subtitle="Bấm một dòng để xem từng người nhận"
      color="dark"
      noPadding
      actions={
        canEdit && (
          <MDBox display="flex" gap={1}>
            <MDButton size="small" color="white" onClick={() => setTesting(true)}>
              <Icon>science</Icon>&nbsp;Phát thử thưởng
            </MDButton>
            <MDButton size="small" color="white" disabled={busy} onClick={resend}>
              <Icon>forward_to_inbox</Icon>&nbsp;Gửi lại thư thiếu
            </MDButton>
          </MDBox>
        )
      }
    >
      {missing && (
        <MDBox px={2} pb={1}>
          <Alert severity="warning">
            Có lần chốt chưa gửi đủ thư. Server tự gửi lại sau mỗi lần chạy, hoặc bấm &quot;Gửi lại
            thư thiếu&quot;.
          </Alert>
        </MDBox>
      )}
      <AdminTable
        rows={settlements}
        emptyText="Chưa chốt lần nào"
        onRowClick={setViewing}
        columns={[
          {
            key: "kind",
            label: "Loại",
            render: (s) => (
              <MDBox display="flex" gap={0.5}>
                <Chip
                  size="small"
                  color={s.kind === 1 ? "warning" : "info"}
                  label={s.kind === 1 ? "Mùa" : "Ngày"}
                />
                {s.test && <Chip size="small" variant="outlined" label="Thử" />}
              </MDBox>
            ),
          },
          { key: "title", label: "Kỳ" },
          { key: "cutoffAt", label: "Chốt lúc", render: (s) => formatDate(s.cutoffAt) },
          {
            key: "players",
            label: "Có hạng / nhận",
            align: "right",
            render: (s) => `${formatNumber(s.players)} / ${formatNumber(s.rewarded)}`,
          },
          {
            key: "sent",
            label: "Đã gửi / đã nhận thư",
            align: "right",
            render: (s) => (
              <MDTypography
                variant="button"
                color={s.sent < s.rewarded ? "error" : "text"}
                fontWeight={s.sent < s.rewarded ? "bold" : "regular"}
              >
                {formatNumber(s.sent)} / {formatNumber(s.claimed)}
              </MDTypography>
            ),
          },
        ]}
      />
      {viewing && <SettlementDialog settlement={viewing} onClose={() => setViewing(null)} />}
      {testing && (
        <TestSettleDialog
          onClose={() => setTesting(false)}
          onDone={() => setTimeout(onChanged, 3000)}
        />
      )}
    </PageCard>
  );
}

SettlementsCard.propTypes = {
  settlements: PropTypes.arrayOf(PropTypes.object).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function RankingCard() {
  const ranking = useFetch(() => api.get("/api/admin/mythic-arena/ranking"), []);
  const [page, setPage] = useState(1);
  const items = ranking.data?.items || [];
  const rows = items.slice((page - 1) * RANKING_PAGE_SIZE, page * RANKING_PAGE_SIZE);

  return (
    <PageCard
      title="Bảng hạng hiện tại"
      subtitle={
        ranking.data
          ? `${formatNumber(
              ranking.data.total
            )} người chơi có hạng (không tính boss), hiện tối đa 500 người đầu`
          : "Nếu chốt ngay bây giờ"
      }
      color="info"
      noPadding
      actions={
        <MDButton size="small" color="white" onClick={() => ranking.reload()}>
          <Icon>refresh</Icon>
        </MDButton>
      }
    >
      {ranking.error && <Alert severity="error">{ranking.error}</Alert>}
      <AdminTable
        loading={ranking.loading}
        rows={rows}
        rowKey="userId"
        page={page}
        size={RANKING_PAGE_SIZE}
        total={items.length}
        onPageChange={setPage}
        emptyText="Chưa có ai đánh đấu trường"
        columns={[
          { key: "rank", label: "Hạng", align: "right", width: 60 },
          {
            key: "name",
            label: "Người chơi",
            render: (r) => (
              <MDBox lineHeight={1.2}>
                <MDTypography
                  component={Link}
                  to={`/players/${r.userId}`}
                  variant="button"
                  color="info"
                  display="block"
                >
                  {r.name}
                </MDTypography>
              </MDBox>
            ),
          },
          {
            key: "trophy",
            label: "Cúp (→ đầu mùa sau)",
            align: "right",
            render: (r) => `${formatNumber(r.trophy)} → ${formatNumber(r.seasonTrophy)}`,
          },
          {
            key: "points",
            label: "Điểm đấu trường",
            align: "right",
            render: (r) => formatNumber(r.points),
          },
          { key: "dailyTier", label: "Mốc ngày", render: (r) => r.dailyTier || "-" },
          { key: "seasonTier", label: "Mốc mùa", render: (r) => r.seasonTier || "-" },
        ]}
      />
    </PageCard>
  );
}

// Tab ghi lên URL (?tab=).
const TABS = [
  { key: "settings", label: "Lịch chốt & thưởng" },
  { key: "history", label: "Lịch sử chốt" },
  { key: "ranking", label: "Bảng hạng" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function MythicArena() {
  const canEdit = useCan(PERMISSIONS.ARENA_EDIT);
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "settings";
  const pickTab = (v) => setParams(v === "settings" ? {} : { tab: v }, { replace: true });
  const overview = useFetch(() => api.get("/api/admin/mythic-arena/overview"), [], 30000);
  const reload = () => overview.reload(true);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="arena" />
        {overview.error && <Alert severity="error">{overview.error}</Alert>}
        {overview.data?.settlements?.some((s) => s.sent < s.rewarded) && tab !== "history" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Có lần chốt chưa gửi đủ thư thưởng — xem tab Lịch sử chốt.
          </Alert>
        )}
        <Tabs
          value={tab}
          onChange={(e, v) => pickTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, maxWidth: 640 }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
          ))}
        </Tabs>
        {overview.data && tab === "settings" && (
          <>
            <ConfigCard overview={overview.data} canEdit={canEdit} onSaved={reload} />
            <MDBox mt={3}>
              <RewardsCard rewards={overview.data.rewards} canEdit={canEdit} onChanged={reload} />
            </MDBox>
          </>
        )}
        {overview.data && tab === "history" && (
          <SettlementsCard
            settlements={overview.data.settlements}
            canEdit={canEdit}
            onChanged={reload}
          />
        )}
        {tab === "ranking" && <RankingCard />}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MythicArena;
